import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Button,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import UpgradeRequestDialog from '../components/UpgradeRequestDialog';
import AccountDeletionRequestDialog from '../components/AccountDeletionRequestDialog';

const Account = () => {
  const { user, tierInfo, loading } = useAuth();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading account information...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Account
        </Typography>
        <Typography variant="body1">Please log in to view your account information.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
      <Box>
        <Typography
          component="h1"
          sx={{ fontWeight: 750, fontSize: { xs: '2rem', md: '2.4rem' }, letterSpacing: '-0.03em', mb: 4 }}
        >
          Account
        </Typography>

      <Grid container spacing={3}>
        {/* User Information Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Personal Information</Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">
                  {user.user_metadata?.first_name || 'N/A'} {user.user_metadata?.last_name || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {user.email}
                  </Typography>
                </Box>
              </Box>

              {user.user_metadata?.company && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Company
                    </Typography>
                    <Typography variant="body1">
                      {user.user_metadata.company}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Member Since
                </Typography>
                <Typography variant="body1">
                  {formatDate(user.created_at)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

         {/* Tier Information Card */}
         <Grid item xs={12} md={6}>
           <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
             <CardContent sx={{ p: 2 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                 <Typography variant="h6">Subscription Tier</Typography>
               </Box>

               <Box sx={{ mb: 2 }}>
                 <Typography variant="body2" color="text.secondary">
                   Tier
                 </Typography>
                 <Typography variant="body1">
                   {tierInfo?.tier ? tierInfo.tier.charAt(0).toUpperCase() + tierInfo.tier.slice(1) : '-'}
                 </Typography>
               </Box>
             </CardContent>
           </Card>
         </Grid>
      </Grid>

      {/* Contact Support Section - Only for Basic users */}
      {tierInfo?.tier === 'basic' && (
        <Box sx={{ mt: 4 }}>
          <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Need More Features?</Typography>
              </Box>
              
              <Typography variant="body1" paragraph>
                Upgrade to Pro for unlimited searches, full data access, expanded search limits, and other advanced features.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={() => setUpgradeDialogOpen(true)}
                  sx={{ 
                    fontWeight: 500
                  }}
                >
                  UPGRADE TO PRO
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Account Management Section */}
      <Box sx={{ mt: 4 }}>
        <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Delete Account</Typography>
            </Box>
            
            <Typography variant="body1" paragraph>
              Need to delete your account? Please send us a request.
            </Typography>
            
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeletionDialogOpen(true)}
              sx={{ 
                fontWeight: 500
              }}
            >
              DELETE ACCOUNT
            </Button>
          </CardContent>
        </Card>
      </Box>
      </Box>
      
      {/* Upgrade Request Dialog */}
      <UpgradeRequestDialog 
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
      />
      
      {/* Account Deletion Request Dialog */}
      <AccountDeletionRequestDialog 
        open={deletionDialogOpen}
        onClose={() => setDeletionDialogOpen(false)}
      />
    </Container>
  );
};

export default Account;
